import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Input } from 'ui-components/components/input';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { Checkbox } from 'ui-components/components/checkbox';
import { DateService } from '../../../../services/date-service';
import { EditOrderRequestPropType } from '../../../../prop-types/edit-order-request';
import messages from '../messages';
import style from './style.module.less';
import CurrencyInput from './currency-input';
import DatePicker from './date-picker';

const OrderSummarySection = ({
  order,
  onOrderUpdated,
  stores = emptyArray,
  autoGenerateOrderNumber = false,
  fieldsWithError = emptyObject,
  toggleAutoGeneratedOrderNumber,
  onAmountSummaryUpdated,
  userDateFormat,
}) => {
  const intl = useIntl();
  const realUserDateFormat = userDateFormat || DateService.getDefaultDateFormat();

  const selectedStore = stores.find((store) => store.storeGuid === order.storeGuid) || (stores.length && stores[0]);
  const title = selectedStore.name || intl.formatMessage(messages.store);

  const { orderNumber, orderDateTime, paidDateTime } = order;
  const showAutoGeneratedCheckbox = selectedStore && selectedStore.willAutoGenOrderNumber;
  const inputValue = autoGenerateOrderNumber ? intl.formatMessage(messages.autoGeneratedOrderNumberInputMessage) : orderNumber;
  const hasError = fieldsWithError?.orderNumber && !autoGenerateOrderNumber && !inputValue.length;
  const { amountSummary } = order;

  return (
    <div className={style['order-summary']}>
      {stores.length > 1 && (
        <div className={style['input-wrapper']}>
          <label className={style['input-label']}>
            <FormattedMessage {...messages.store} />
          </label>
          <DropdownMenu dropdownMenuShouldHaveWidthOfButton fullWidth variant='form'>
            <DropdownMenuToggler className={style['store-dropdown-toggler']} title={title}>
              {title}
            </DropdownMenuToggler>
            {stores.map(({ name, storeGuid }) => (
              <DropdownMenuItem
                key={storeGuid}
                onClick={() => onOrderUpdated('storeGuid', storeGuid)}
                selected={selectedStore?.storeGuid === storeGuid}
              >
                {name}
              </DropdownMenuItem>
            ))}
          </DropdownMenu>
        </div>
      )}
      {stores.length <= 1 && (
        <div className={style['input-wrapper']}>
          <label className={style['store-label']}>
            <FormattedMessage {...messages.store} />
          </label>
          <span>{title}</span>
        </div>
      )}
      <div className={style['input-wrapper']}>
        <label className={style['input-label']}>
          <FormattedMessage {...messages.orderNumber} />
        </label>
        <Input
          className={style['order-summary-input']}
          data-testid='Order #'
          disabled={autoGenerateOrderNumber}
          hasError={hasError}
          onChange={(newOrderNumber) => onOrderUpdated('orderNumber', newOrderNumber)}
          value={inputValue}
        />
        {showAutoGeneratedCheckbox && (
          <div className={style['checkbox-row']}>
            <Checkbox
              checked={autoGenerateOrderNumber}
              className={style.checkbox}
              label={intl.formatMessage(messages.autogenerateOrderNumber)}
              onChange={toggleAutoGeneratedOrderNumber}
            />
          </div>
        )}
      </div>
      <DatePicker
        allowPastDate={orderDateTime}
        labelTranslation={messages.orderDate}
        name='orderDateTime'
        onSelectedDateChanged={onOrderUpdated}
        userDateFormat={realUserDateFormat}
        value={order.orderDateTime}
      />
      <DatePicker
        allowPastDate={paidDateTime}
        labelTranslation={messages.paidDate}
        name='paidDateTime'
        onSelectedDateChanged={onOrderUpdated}
        userDateFormat={realUserDateFormat}
        value={order.paidDateTime}
      />
      <CurrencyInput
        currencyAmount={amountSummary.shippingPaid}
        labelTranslation={messages.shippingPaid}
        name='shippingPaid'
        onChange={onAmountSummaryUpdated}
      />
      <CurrencyInput
        currencyAmount={amountSummary.taxPaid}
        labelTranslation={messages.taxPaid}
        name='taxPaid'
        onChange={onAmountSummaryUpdated}
      />
      <CurrencyInput
        currencyAmount={amountSummary.totalPaid}
        labelTranslation={messages.totalPaid}
        name='totalPaid'
        onChange={onAmountSummaryUpdated}
      />
    </div>
  );
};

OrderSummarySection.propTypes = {
  order: EditOrderRequestPropType.isRequired,
  onOrderUpdated: PropTypes.func.isRequired,
  onAmountSummaryUpdated: PropTypes.func.isRequired,
  stores: PropTypes.array,
  fieldsWithError: PropTypes.object,
  userDateFormat: PropTypes.string,
  autoGenerateOrderNumber: PropTypes.bool,
  toggleAutoGeneratedOrderNumber: PropTypes.func.isRequired,
};

export default memo(OrderSummarySection);
